<template>
	<div v-if="!hideEmptySection" class="choose-car">
		<div class="choose-car__container">
			<h2 v-if="!noTitle" :data-aos="disableAos ? undefined : 'fade-up'" class="choose-car__title">
				{{ $t('modules.home_page_module.home_page_choose_car.choose_vehicle') }}
			</h2>

			<div ref="carsListingRef" class="choose-car__main">
				<div v-if="!oneGroup" class="choose-car__segments">
					<ButtonCustom
						v-for="(group, i) in groups"
						v-show="i < 8"
						:key="i"
						class="choose-car__segment-btn"
						:class="{ 'choose-car__segment-btn--active': group.id === choosenGroup?.id }"
						@click="chooseGroup(group)">
						{{
							typeof group.name === 'string'
								? groupMap[group.name]
									? i18nService.$tDynamic('data.group_map_translations', groupMapTranslations[group.name])
									: group.name
								: group.name
						}}
					</ButtonCustom>
				</div>

				<Swiper
					class="choose-car__swiper"
					slidesPerView="auto"
					:space-between="16"
					@after-init="
						(swiper) => {
							// PAGE TRANSITION MODE OUT-IN FIX
							useTimeoutFn(() => {
								swiper.slideNext(0)
								swiper.slidePrev(0)
							}, 400)
						}
					"
					@slide-change="closeTooltipsOnEachSingleCarRef()">
					<template v-if="cars && cars?.length">
						<SwiperSlide v-for="(car, j) in cars" :key="car.id" class="choose-car__swiper-slide">
							<CarsSingleCar
								ref="singleCarRefs"
								:car="car"
								:index="j"
								home
								:whiteCard="whiteCard"
								:modal-form="props.modalForm"
								:min-months="props.minMonths"
								:max-months="props.maxMonths"
								:noBooker="props.noBooker" />
						</SwiperSlide>
						<SwiperSlide class="choose-car__swiper-slide">
							<NuxtLink :to="listingLink" class="choose-car__placeholder" :class="{ 'choose-car__placeholder--white': whiteCard }">
								<span class="choose-car__placeholder-icon">
									<GlobalIcon name="arrow-right" :height="24" />
								</span>
								<span class="choose-car__placeholder-text">
									{{ $t('components.news.section.see_all') }}
								</span>
							</NuxtLink>
						</SwiperSlide>
					</template>
					<template v-else>
						<SwiperSlide v-for="k in 8" :key="`skeleton-${k}`" :slidesPerView="4" :space-between="16" class="choose-car__swiper-slide">
							<Skeletor class="choose-car__skeletor" round :height="497" />
						</SwiperSlide>
					</template>

					<GlobalNewsSwiperNav v-if="cars && cars?.length" :gray-bg="props.grayBg" />
					<GlobalNewsSwiperNav v-else :gray-bg="props.grayBg" />
				</Swiper>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Skeletor } from 'vue-skeletor'
// import { format } from 'date-fns'

const props = defineProps<{
	modalForm?: boolean
	minMonths?: number
	maxMonths?: number
	grayBg?: boolean
	noTitle?: boolean
	whiteCard?: boolean
	oneGroup?: EGroupName
	disableAos?: boolean
	init?: boolean
	noBooker?: boolean
}>()

const singleCarRefs = ref<HTMLElement[]>([])

const closeTooltipsOnEachSingleCarRef = () => {
	singleCarRefs.value.forEach((ref: any) => {
		if (ref?.changeTooltip) {
			ref.changeTooltip(false)
		}
	})
}

const groups = ref<IProductGroupModel[]>([])
const choosenGroup = ref<IProductGroupModel | null>(null)
const cars = ref<IProductModel[]>([])
const hasFetchedOnce = ref(false)

// only relevant for the single-group case (e.g. tesla/bus/delivery pages) - when a group can be
// switched via the segment buttons, an empty result for one group shouldn't hide the whole section
const hideEmptySection = computed(() => !!props.oneGroup && hasFetchedOnce.value && cars.value.length === 0)
const i18nService = usei18nService()
// const bookerStore = useBookerStore()
const carsStore = useCarsStore()
const localePath = useLocalePath()

const listingLink = computed(() => {
	const query = choosenGroup.value?.name ? { group: String(choosenGroup.value.name) } : undefined
	return localePath({ name: 'cars', query })
})

const fetchGroups = async () => {
	try {
		const response = await carsStore.fetchAllGroups()
		groups.value = filterPublicGroups(response)

		if (props.oneGroup) {
			choosenGroup.value = groups.value.find((group) => group.name === props.oneGroup) || null
		} else {
			choosenGroup.value = groups.value[0]
		}
		return response
	} catch (error) {
		logger.error(error)
	}
}

if (props?.init) {
	await fetchGroups()
}

let currentAbortController: AbortController | null = null

const fetchCars = async () => {
	if (!choosenGroup.value) {
		hasFetchedOnce.value = true
		return
	}
	cars.value = []

	if (currentAbortController) {
		currentAbortController.abort()
	}
	currentAbortController = new AbortController()
	const signal = currentAbortController.signal

	try {
		const searches: IProductParameterSearch[] = [
			{
				id: 'group_id',
				values: [choosenGroup.value.id],
			},
		]

		const filters: IProductFilter[] = []

		filters.push({
			id: 'has_price',
			value: '1',
		})

		const response = await ApiService.fetchProductsByParameters(searches, filters, 1, 8, undefined, CARS_SLIDER_COUNT_PER_SEGMENT, { signal })

		if (signal.aborted) return
		cars.value = response?.response ?? []
		return response
	} catch (error: any) {
		if (error?.name !== 'AbortError' && !signal.aborted) {
			logger.error(error)
		}
	} finally {
		if (!signal.aborted) hasFetchedOnce.value = true
	}
}

const initSection = async () => {
	if (!groups.value.length) await fetchGroups()
	await fetchCars()
}

// Analytics
const carsListingRef = ref<HTMLElement | null>(null)
useSearchListingViewed(carsListingRef, cars, 'Karuzela samochodów')

const chooseGroup = (group: IProductGroupModel) => {
	choosenGroup.value = group
	fetchCars()
}

onMounted(() => {
	initSection()
})
</script>

<style lang="scss">
@import 'vue-skeletor/dist/vue-skeletor.css';
</style>
<style lang="scss" scoped src="./HomePageChooseCar.scss"></style>
